/**
 * Emergent Marketplace — Phase 3
 * AdminDashboard.jsx
 * Architectural Layer: Presentation → Admin
 *
 * Full admin panel: platform analytics, GMV, vendor management,
 * payout queue, admin alert resolution with retry transfers.
 * Design: Authoritative precision — graphite, white, signal red/green.
 * Font: Space Grotesk (display) + Inter (body) — purposely utilitarian
 * Note: admin tools are intentionally dense and functional, not decorative.
 */

import { useState, useEffect } from "react";
import {
  BarChart2, Users, ShoppingBag, DollarSign, AlertTriangle,
  CheckCircle, RefreshCw, ChevronDown, ChevronUp, Search,
  TrendingUp, Store, Clock, X, ArrowUpRight, Loader,
  ExternalLink, Bell
} from "lucide-react";
import { apiFetch } from "./api";

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap');

  .ad-root {
    --graphite:    #1a1e2a;
    --graphite-m:  #2a3040;
    --graphite-l:  #3a4458;
    --silver:      #8a96a8;
    --silver-l:    #c0c8d8;
    --white:       #ffffff;
    --bg:          #f4f5f7;
    --surface:     #ffffff;
    --border:      #e0e4ec;
    --green:       #0e7a4a;
    --green-bg:    #e8f7f0;
    --green-border:#a0d8c0;
    --red:         #c02828;
    --red-bg:      #fdf0f0;
    --red-border:  #e8b0b0;
    --amber:       #a06010;
    --amber-bg:    #fef8ea;
    --amber-border:#e0c070;
    --blue:        #1a50a8;
    --blue-bg:     #eef4fd;
    font-family: 'Space Grotesk', sans-serif;
    background: var(--bg);
    color: var(--graphite);
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  /* Top bar */
  .ad-topbar {
    background: var(--graphite);
    padding: 14px 28px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
  }

  .ad-topbar-title {
    font-size: 16px;
    font-weight: 700;
    color: var(--white);
    letter-spacing: 0.02em;
  }

  .ad-topbar-sub {
    font-size: 11px;
    color: var(--silver);
    margin-top: 2px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .ad-topbar-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .ad-alert-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    background: rgba(192,40,40,0.15);
    border: 1px solid rgba(192,40,40,0.3);
    color: #ff8080;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
  }

  /* Nav tabs */
  .ad-nav {
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    padding: 0 28px;
    display: flex;
    gap: 0;
  }

  .ad-nav-btn {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 12px 18px;
    border: none; background: none;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 13px; font-weight: 500;
    color: var(--silver);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.15s;
  }

  .ad-nav-btn.active { color: var(--graphite); border-bottom-color: var(--graphite); font-weight: 600; }
  .ad-nav-btn:hover  { color: var(--graphite-l); }

  .ad-nav-badge {
    background: var(--red);
    color: white;
    border-radius: 10px;
    padding: 1px 7px;
    font-size: 10px;
    font-weight: 700;
  }

  /* Content */
  .ad-content { padding: 24px 28px 40px; }

  /* ── Stats grid ──────────────────────────────────────────────────────── */
  .ad-stats {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 14px;
    margin-bottom: 24px;
  }

  .ad-stat {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 18px 20px;
  }

  .ad-stat-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--silver);
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .ad-stat-value {
    font-size: 28px;
    font-weight: 700;
    color: var(--graphite);
    line-height: 1;
    margin-bottom: 5px;
  }

  .ad-stat-sub {
    font-size: 12px;
    color: var(--silver);
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .ad-delta-up   { color: var(--green); }
  .ad-delta-down { color: var(--red); }

  /* ── Two-col layout ──────────────────────────────────────────────────── */
  .ad-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }

  .ad-panel {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    overflow: hidden;
  }

  .ad-panel-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 18px 10px;
    border-bottom: 1px solid var(--border);
  }

  .ad-panel-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--graphite);
    display: flex;
    align-items: center;
    gap: 7px;
  }

  .ad-panel-link {
    font-size: 12px;
    color: var(--blue);
    cursor: pointer;
    background: none;
    border: none;
    font-family: 'Space Grotesk', sans-serif;
    display: flex;
    align-items: center;
    gap: 3px;
  }

  /* Top vendors table */
  .ad-vendor-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 18px;
    border-bottom: 1px solid #f4f5f7;
    font-size: 13px;
  }

  .ad-vendor-row:last-child { border-bottom: none; }

  .ad-vendor-rank {
    font-size: 11px;
    font-weight: 700;
    color: var(--silver);
    width: 20px;
    text-align: center;
  }

  .ad-vendor-name { flex: 1; font-weight: 500; color: var(--graphite); }
  .ad-vendor-gmv  { font-weight: 700; color: var(--graphite); }
  .ad-vendor-count { font-size: 11px; color: var(--silver); }

  /* Status breakdown */
  .ad-status-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 18px;
    border-bottom: 1px solid #f4f5f7;
    font-size: 13px;
  }

  .ad-status-row:last-child { border-bottom: none; }

  .ad-status-dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    margin-right: 8px;
  }

  /* ── Alerts section ──────────────────────────────────────────────────── */
  .ad-alerts-section {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 20px;
  }

  .ad-alert-card {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 14px 18px;
    border-bottom: 1px solid #f4f5f7;
    animation: ad-slidein 0.2s ease;
  }

  @keyframes ad-slidein {
    from { opacity: 0; transform: translateX(-4px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .ad-alert-card:last-child { border-bottom: none; }

  .ad-alert-icon {
    width: 34px; height: 34px;
    border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }

  .ad-alert-icon.transfer { background: var(--red-bg); color: var(--red); }
  .ad-alert-icon.missing  { background: var(--amber-bg); color: var(--amber); }

  .ad-alert-body { flex: 1; }

  .ad-alert-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--graphite);
    margin: 0 0 3px;
  }

  .ad-alert-meta {
    font-size: 11px;
    color: var(--silver);
    margin: 0 0 10px;
  }

  .ad-alert-actions { display: flex; gap: 8px; }

  .ad-btn-retry {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 7px;
    border: none;
    background: var(--graphite);
    color: white;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
  }

  .ad-btn-retry:hover { background: var(--graphite-m); }
  .ad-btn-retry:disabled { opacity: 0.5; cursor: not-allowed; }

  .ad-btn-resolve {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 7px;
    border: 1px solid var(--border);
    background: none;
    color: var(--silver);
    font-family: 'Space Grotesk', sans-serif;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.15s;
  }

  .ad-btn-resolve:hover { border-color: var(--silver); color: var(--graphite); }

  /* ── Payout queue ────────────────────────────────────────────────────── */
  .ad-payout-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 18px;
    border-bottom: 1px solid #f4f5f7;
    font-size: 13px;
    gap: 12px;
  }

  .ad-payout-row:last-child { border-bottom: none; }

  .ad-payout-order { font-weight: 600; color: var(--graphite); }
  .ad-payout-vendor { font-size: 11px; color: var(--silver); }
  .ad-payout-amount { font-weight: 700; color: var(--graphite); white-space: nowrap; }

  .ad-payout-reason {
    font-size: 11px;
    color: var(--amber);
    background: var(--amber-bg);
    padding: 2px 8px;
    border-radius: 6px;
  }

  /* Search bar */
  .ad-search {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 8px 14px;
    margin-bottom: 16px;
  }

  .ad-search input {
    background: none; border: none; outline: none;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 13px; color: var(--graphite); width: 100%;
  }

  .ad-search input::placeholder { color: var(--silver-l); }
`;

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_ANALYTICS = {
  gmv_total:           24840.00,
  gmv_this_month:      3420.00,
  platform_revenue:    1242.00,
  revenue_this_month:  171.00,
  total_orders:        187,
  orders_this_month:   28,
  active_vendors:      12,
  pending_vendors:     3,
  total_customers:     94,
  top_vendors: [
    { vendor_id: "v1", store_name: "Sacred Roots",     gmv: 6840, order_count: 52 },
    { vendor_id: "v2", store_name: "The Still Point",  gmv: 4200, order_count: 38 },
    { vendor_id: "v3", store_name: "Luminous Path",    gmv: 3900, order_count: 31 },
    { vendor_id: "v4", store_name: "Shadow & Light",   gmv: 2640, order_count: 21 },
    { vendor_id: "v5", store_name: "Root & Rise",      gmv: 1980, order_count: 16 },
  ],
  orders_by_status: {
    paid: 42, processing: 8, shipped: 15, delivered: 118, cancelled: 4
  },
};

const MOCK_ALERTS = [
  {
    id: "a1",
    type: "transfer_failed",
    order_id: "ord-B8X2M4",
    vendor_id: "v3",
    vendor_name: "Luminous Path",
    amount: 87.50,
    error: "Stripe account not enabled for payouts",
    created_at: "2026-03-11T09:14:00Z",
    resolved: false,
  },
  {
    id: "a2",
    type: "missing_stripe_account",
    order_id: "ord-C4P9W1",
    vendor_id: "v5",
    vendor_name: "Root & Rise",
    amount: 42.00,
    created_at: "2026-03-09T14:30:00Z",
    resolved: false,
  },
];

const MOCK_PENDING_PAYOUTS = [
  { order_id: "ord-B8X2M4", vendor_name: "Luminous Path", amount: 87.50, reason: "Transfer failed" },
  { order_id: "ord-C4P9W1", vendor_name: "Root & Rise",   amount: 42.00, reason: "No Stripe account" },
  { order_id: "ord-D1N7Q3", vendor_name: "Sacred Roots",  amount: 22.80, reason: "Pending transfer" },
];

const STATUS_COLORS = {
  paid: "#0e7a4a", processing: "#1a50a8", shipped: "#6a30a0",
  delivered: "#0e7a4a", cancelled: "#c02828",
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const [activeTab, setActiveTab]   = useState("analytics");
  const [analytics, setAnalytics]   = useState(null);
  const [alerts, setAlerts]         = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [retrying, setRetrying]     = useState(null);
  const [resolving, setResolving]   = useState(null);

  const unresolvedCount = alerts.filter(a => !a.resolved).length;

  useEffect(() => {
    apiFetch("/admin/marketplace/analytics").then(setAnalytics).catch(() => {});
    apiFetch("/admin/marketplace/alerts").then(setAlerts).catch(() => {});
    apiFetch("/admin/marketplace/payouts/pending").then(setPendingPayouts).catch(() => {});
  }, []);

  const retryTransfer = async (alertId) => {
    setRetrying(alertId);
    try {
      await apiFetch(`/admin/marketplace/alerts/${alertId}/retry-transfer`, { method: "POST" });
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, resolved: true } : a));
    } finally {
      setRetrying(null);
    }
  };

  const resolveAlert = async (alertId) => {
    setResolving(alertId);
    try {
      await apiFetch(`/admin/marketplace/alerts/${alertId}/resolve`, { method: "POST", body: JSON.stringify({}) });
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, resolved: true } : a));
    } finally {
      setResolving(null);
    }
  };

  const activeAlerts = alerts.filter(a => !a.resolved);

  return (
    <>
      <style>{styles}</style>
      <div className="ad-root">

        {/* Top bar */}
        <div className="ad-topbar">
          <div>
            <div className="ad-topbar-title">11/11 Collective Connection Marketplace — Admin</div>
            <div className="ad-topbar-sub">Platform operations dashboard</div>
          </div>
          <div className="ad-topbar-right">
            {unresolvedCount > 0 && (
              <div className="ad-alert-pill" onClick={() => setActiveTab("alerts")}>
                <AlertTriangle size={13} /> {unresolvedCount} alert{unresolvedCount > 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>

        {/* Nav */}
        <div className="ad-nav">
          {[
            { id: "analytics", label: "Analytics",    icon: BarChart2 },
            { id: "vendors",   label: "Vendors",      icon: Store },
            { id: "payouts",   label: "Payout Queue", icon: DollarSign },
            { id: "alerts",    label: "Alerts",       icon: AlertTriangle, badge: unresolvedCount },
          ].map(({ id, label, icon: Icon, badge }) => (
            <button
              key={id}
              className={`ad-nav-btn ${activeTab === id ? "active" : ""}`}
              onClick={() => setActiveTab(id)}
            >
              <Icon size={15} /> {label}
              {badge > 0 && <span className="ad-nav-badge">{badge}</span>}
            </button>
          ))}
        </div>

        <div className="ad-content">

          {/* ── Analytics tab ── */}
          {activeTab === "analytics" && analytics && (
            <>
              <div className="ad-stats">
                <div className="ad-stat">
                  <div className="ad-stat-label"><DollarSign size={12} /> GMV total</div>
                  <div className="ad-stat-value">${analytics.gmv_total.toLocaleString()}</div>
                  <div className="ad-stat-sub ad-delta-up">
                    <TrendingUp size={11} /> ${analytics.gmv_this_month.toLocaleString()} this month
                  </div>
                </div>
                <div className="ad-stat">
                  <div className="ad-stat-label"><DollarSign size={12} /> Platform revenue</div>
                  <div className="ad-stat-value">${analytics.platform_revenue.toLocaleString()}</div>
                  <div className="ad-stat-sub">${analytics.revenue_this_month} this month</div>
                </div>
                <div className="ad-stat">
                  <div className="ad-stat-label"><ShoppingBag size={12} /> Orders</div>
                  <div className="ad-stat-value">{analytics.total_orders}</div>
                  <div className="ad-stat-sub">{analytics.orders_this_month} this month</div>
                </div>
                <div className="ad-stat">
                  <div className="ad-stat-label"><Store size={12} /> Active vendors</div>
                  <div className="ad-stat-value">{analytics.active_vendors}</div>
                  <div className="ad-stat-sub">
                    <span style={{ color: "var(--amber)" }}>{analytics.pending_vendors} pending approval</span>
                  </div>
                </div>
                <div className="ad-stat">
                  <div className="ad-stat-label"><Users size={12} /> Customers</div>
                  <div className="ad-stat-value">{analytics.total_customers}</div>
                  <div className="ad-stat-sub">Unique buyers</div>
                </div>
              </div>

              <div className="ad-two-col">
                {/* Top vendors */}
                <div className="ad-panel">
                  <div className="ad-panel-head">
                    <span className="ad-panel-title"><TrendingUp size={14} /> Top Vendors by GMV</span>
                  </div>
                  {analytics.top_vendors.map((v, i) => (
                    <div key={v.vendor_id} className="ad-vendor-row">
                      <span className="ad-vendor-rank">{i + 1}</span>
                      <span className="ad-vendor-name">{v.store_name}</span>
                      <span className="ad-vendor-count">{v.order_count} orders</span>
                      <span className="ad-vendor-gmv">${v.gmv.toLocaleString()}</span>
                    </div>
                  ))}
                </div>

                {/* Orders by status */}
                <div className="ad-panel">
                  <div className="ad-panel-head">
                    <span className="ad-panel-title"><ShoppingBag size={14} /> Orders by Status</span>
                  </div>
                  {Object.entries(analytics.orders_by_status).map(([status, count]) => (
                    <div key={status} className="ad-status-row">
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div className="ad-status-dot" style={{ background: STATUS_COLORS[status] || "#888" }} />
                        <span style={{ textTransform: "capitalize", fontWeight: 500 }}>{status}</span>
                      </div>
                      <span style={{ fontWeight: 700 }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── Vendors tab ── */}
          {activeTab === "vendors" && (
            <div className="ad-panel" style={{ maxWidth: 800 }}>
              <div className="ad-panel-head">
                <span className="ad-panel-title">Vendor Management</span>
                <button className="ad-panel-link" onClick={() => setActiveTab("vendors")}>
                  View pending ({analytics.pending_vendors}) <ArrowUpRight size={12} />
                </button>
              </div>
              <div style={{ padding: "16px 18px", fontSize: 13, color: "var(--silver)" }}>
                Mount <code style={{ background: "#f4f5f7", padding: "2px 6px", borderRadius: 4 }}>
                  VendorApprovals
                </code> component here — already built in Phase 1.
              </div>
            </div>
          )}

          {/* ── Payout queue tab ── */}
          {activeTab === "payouts" && (
            <div className="ad-panel" style={{ maxWidth: 800 }}>
              <div className="ad-panel-head">
                <span className="ad-panel-title"><DollarSign size={14} /> Pending Payouts</span>
                <span style={{ fontSize: 12, color: "var(--silver)" }}>
                  ${pendingPayouts.reduce((s, p) => s + (p.payment?.amount_total || 0), 0).toFixed(2)} total outstanding
                </span>
              </div>
              {pendingPayouts.map((p, i) => (
                <div key={i} className="ad-payout-row">
                  <div style={{ flex: 1 }}>
                    <div className="ad-payout-order">Order #{(p.id || "").slice(-6)}</div>
                    <div className="ad-payout-vendor">{p.vendor_splits?.[0]?.vendor_name || "Vendor"}</div>
                  </div>
                  <span className="ad-payout-reason">pending transfer</span>
                  <span className="ad-payout-amount">${(p.vendor_splits?.[0]?.vendor_payout || 0).toFixed(2)}</span>
                  <button
                    className="ad-btn-retry"
                    style={{ fontSize: 11, padding: "5px 11px" }}
                    onClick={() => setActiveTab("alerts")}
                  >
                    Resolve →
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ── Alerts tab ── */}
          {activeTab === "alerts" && (
            <>
              <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 700, margin: "0 0 3px" }}>
                    Transfer Alerts
                  </h2>
                  <p style={{ fontSize: 13, color: "var(--silver)", margin: 0 }}>
                    Failed or missing vendor payouts requiring manual action.
                  </p>
                </div>
              </div>

              <div className="ad-alerts-section">
                <div className="ad-panel-head">
                  <span className="ad-panel-title">
                    <AlertTriangle size={14} />
                    Unresolved ({activeAlerts.length})
                  </span>
                </div>

                {activeAlerts.length === 0 ? (
                  <div style={{ padding: "40px", textAlign: "center", color: "var(--silver)", fontSize: 14 }}>
                    <CheckCircle size={28} style={{ opacity: 0.3, marginBottom: 10, display: "block", margin: "0 auto 10px" }} />
                    No active alerts — all transfers are healthy.
                  </div>
                ) : (
                  activeAlerts.map(alert => (
                    <div key={alert.id} className="ad-alert-card">
                      <div className={`ad-alert-icon ${alert.type === "transfer_failed" ? "transfer" : "missing"}`}>
                        {alert.type === "transfer_failed"
                          ? <AlertTriangle size={16} />
                          : <Clock size={16} />
                        }
                      </div>
                      <div className="ad-alert-body">
                        <p className="ad-alert-title">
                          {alert.type === "transfer_failed"
                            ? `Transfer failed — ${alert.vendor_name}`
                            : `Missing Stripe account — ${alert.vendor_name}`
                          }
                        </p>
                        <p className="ad-alert-meta">
                          Order #{alert.order_id.slice(-6)} ·
                          ${alert.amount.toFixed(2)} ·
                          {alert.error ? ` ${alert.error} · ` : " "}
                          {formatDate(alert.created_at)}
                        </p>
                        <div className="ad-alert-actions">
                          {alert.type === "transfer_failed" && (
                            <button
                              className="ad-btn-retry"
                              onClick={() => retryTransfer(alert.id)}
                              disabled={retrying === alert.id}
                            >
                              {retrying === alert.id
                                ? <><Loader size={12} style={{ animation: "spin 1s linear infinite" }} /> Retrying...</>
                                : <><RefreshCw size={12} /> Retry transfer</>
                              }
                            </button>
                          )}
                          <button
                            className="ad-btn-resolve"
                            onClick={() => resolveAlert(alert.id)}
                            disabled={resolving === alert.id}
                          >
                            {resolving === alert.id
                              ? "Resolving..."
                              : <><CheckCircle size={12} /> Mark resolved</>
                            }
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}

                {alerts.filter(a => a.resolved).length > 0 && (
                  <div style={{ padding: "10px 18px", fontSize: 12, color: "var(--silver)", borderTop: "1px solid var(--border)" }}>
                    {alerts.filter(a => a.resolved).length} resolved alert(s) hidden.
                    <button style={{ background: "none", border: "none", color: "var(--blue)", fontSize: 12, cursor: "pointer", marginLeft: 6 }}>
                      Show resolved
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
}
